import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowUpDown } from "lucide-react";
import { useQueries } from "@tanstack/react-query";
import { fetchEventResults } from "@/api/results";
import type { EventResultsData } from "@/types/results";
import { isUpcomingEvent, isPastEvent } from "@/lib/events";
import { EventCard } from "./EventCard";
import type { Quizrun } from "@/types/domain";
import { eventResultsKeys } from "@/api/queryKeys";
import { logger, serializeError } from "@/lib/logger";

interface EventListProps {
  quizruns: Quizrun[];
}

type SortDirection = "asc" | "desc";
type Tab = "upcoming" | "past";

export const EventList = ({ quizruns }: EventListProps) => {
  const upcomingQuizruns = useMemo(() => quizruns.filter(isUpcomingEvent), [quizruns]);
  const pastQuizruns = useMemo(() => quizruns.filter(isPastEvent), [quizruns]);

  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const sortParam = searchParams.get("sort");
  const defaultTab: Tab = upcomingQuizruns.length > 0 ? "upcoming" : "past";
  const activeTab: Tab = tabParam === "upcoming" || tabParam === "past" ? tabParam : defaultTab;
  const sortDirection: SortDirection =
    sortParam === "asc" || sortParam === "desc"
      ? sortParam
      : activeTab === "upcoming" ? "asc" : "desc";

  const setActiveTab = (tab: Tab) => {
    setSearchParams({ tab }, { replace: true });
  };

  const resultsQueries = useQueries({
    queries: quizruns.map((qr) => ({
      queryKey: eventResultsKeys.summary(qr.id),
      queryFn: async (): Promise<EventResultsData | null> => {
        try {
          return await fetchEventResults(qr.id);
        } catch (err) {
          logger.error("Failed to fetch quizrun results", {
            quizrunId: qr.id,
            ...serializeError(err),
          });
          return null;
        }
      },
      staleTime: 5 * 60 * 1000,
    })),
  });

  const currentQuizruns = activeTab === "upcoming" ? upcomingQuizruns : pastQuizruns;

  const sortedQuizruns = useMemo(() => {
    return [...currentQuizruns].sort((a, b) => {
      const aValue = a.date_start;
      const bValue = b.date_start;

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [currentQuizruns, sortDirection]);

  const toggleSortDirection = () => {
    // Toggle sort by flipping the URL param
    setSearchParams(
      { tab: activeTab, sort: sortDirection === "asc" ? "desc" : "asc" },
      { replace: true }
    );
  };

  const getResultsQuery = (quizrunId: number) => {
    const index = quizruns.findIndex((qr) => qr.id === quizrunId);
    if (index === -1) return undefined;
    return resultsQueries[index];
  };

  const SortButton = ({ children }: { children: React.ReactNode }) => (
    <button
      type="button"
      onClick={toggleSortDirection}
      className="inline-flex items-center gap-1 text-xs font-medium uppercase tracking-wider px-3 py-1.5 rounded-full border transition-colors bg-primary/10 border-primary/30 text-primary"
    >
      {children}
      <ArrowUpDown className="h-3 w-3" />
    </button>
  );

  if (quizruns.length === 0) {
    return (
      <p className="text-center text-muted-foreground py-12">
        No quizruns found.
      </p>
    );
  }

  return (
    <div className="space-y-5">
      {/* Tab bar */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setActiveTab("upcoming")}
          className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
            activeTab === "upcoming"
              ? "bg-primary text-primary-foreground shadow-sm"
              : "bg-muted/60 text-muted-foreground hover:text-foreground hover:bg-muted"
          }`}
        >
          Upcoming
          {upcomingQuizruns.length > 0 && (
            // a11y: opacity-70 blended text into background, dropping contrast below 4.5:1
            <span className="ml-1.5 text-xs">({upcomingQuizruns.length})</span>
          )}
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("past")}
          className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
            activeTab === "past"
              ? "bg-primary text-primary-foreground shadow-sm"
              : "bg-muted/60 text-muted-foreground hover:text-foreground hover:bg-muted"
          }`}
        >
          Past Results
          {pastQuizruns.length > 0 && (
            // a11y: opacity-70 blended text into background, dropping contrast below 4.5:1
            <span className="ml-1.5 text-xs">({pastQuizruns.length})</span>
          )}
        </button>
      </div>

      {/* Sort controls — only show when there's enough items */}
      {sortedQuizruns.length > 3 && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground mr-1">Sort by</span>
          <SortButton>Date</SortButton>
        </div>
      )}

      {/* Event cards */}
      <div className="space-y-2 md:space-y-3">
        {sortedQuizruns.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">
            {activeTab === "upcoming"
              ? "No upcoming events right now. Check back soon!"
              : "No past events yet."}
          </p>
        ) : (
          sortedQuizruns.map((qr, index) => (
            <EventCard
              key={qr.id}
              quizrun={qr}
              resultsQuery={getResultsQuery(qr.id)}
              // Ramp gradient starts at the top card; reset per view.
              colorSeed={index + 1}
            />
          ))
        )}
      </div>
    </div>
  );
};
